// EmailIntegration.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Mail,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Settings,
  TrendingUp,
  Shield,
  Clock,
  Database,
} from "lucide-react";
import api from "../../services/api";
import {
  TrendUp,
  SearchNormal,
  ExportCurve,
  ImportCurve,
  Add,
  Sort,
  Bubble,
} from "iconsax-reactjs";

const EmailIntegration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncing, setSyncing] = useState({});
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadConnections();
    loadSyncLogs();
    loadStats();

    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const provider = searchParams.get("provider");
    const email = searchParams.get("email");

    if (success === "true" && provider) {
      console.log(`${provider} email connected successfully: ${email}`);
      loadConnections();
    } else if (error) {
      console.error(`Failed to connect email: ${error}`);
    }
  }, [searchParams]);

  const loadConnections = async () => {
    try {
      const response = await api.getEmailConnectionStatus();
      setConnections(response);
    } catch (error) {
      console.error("Failed to load connections:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncLogs = async () => {
    try {
      const response = await api.getEmailSyncLogs(10);
      setSyncLogs(response.logs || []);
    } catch (error) {
      console.error("Failed to load sync logs:", error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.getEmailAnalyticsSummary({});
      setStats(response);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const connectEmail = async (provider) => {
    try {
      const response = await api.connectEmail(provider);
      window.location.href = response.authUrl;
    } catch (error) {
      console.error("Failed to connect email:", error);
    }
  };

  const disconnectEmail = async (provider) => {
    try {
      await api.disconnectEmail(provider);
      loadConnections();
    } catch (error) {
      console.error("Failed to disconnect email:", error);
    }
  };

  const toggleSync = async (provider, enabled) => {
    try {
      await api.toggleEmailSync(provider, enabled);
      loadConnections();
    } catch (error) {
      console.error("Failed to toggle sync:", error);
    }
  };

  const syncNow = async (provider) => {
    setSyncing({ ...syncing, [provider]: true });
    try {
      const response = await api.syncEmails({
        provider,
        syncType: "incremental",
      });
      console.log(
        `Synced ${response.messagesSynced} messages, analyzed ${response.messagesAnalyzed}`
      );
      loadSyncLogs();
      loadStats();
      loadConnections();
    } catch (error) {
      console.error("Failed to sync emails:", error);
    } finally {
      setSyncing({ ...syncing, [provider]: false });
    }
  };

  const getProviderIcon = (provider) => {
    if (provider === "microsoft") {
      return (
        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
          <Mail className="w-6 h-6 text-white" />
        </div>
      );
    } else if (provider === "google") {
      return (
        <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
          <Mail className="w-6 h-6 text-white" />
        </div>
      );
    }
  };

  const formatDate = (date) => {
    if (!date) return "Never";
    return new Date(date).toLocaleString();
  };

  const getSyncStatusBadge = (log) => {
    const statusConfig = {
      completed: {
        bg: "bg-green-500/10",
        text: "text-green-400",
        label: "Completed",
      },
      running: {
        bg: "bg-orange-500/10",
        text: "text-orange-400",
        label: "Running",
      },
      failed: { bg: "bg-red-500/10", text: "text-red-400", label: "Failed" },
      cancelled: {
        bg: "bg-gray-500/10",
        text: "text-gray-400",
        label: "Cancelled",
      },
    };

    const config = statusConfig[log.status] || statusConfig.completed;
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  const StatCard = ({ icon: Icon, title, value, iconBg }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-400 mb-2">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Email Integration
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Connect your email accounts for AI-powered analysis and insights
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={() => navigate("/email/legal-holds")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <Shield size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Legal Holds</span>
            </button>
            <button
              onClick={() => navigate("/email/analytics")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <TrendUp size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Email Analytics</span>
            </button>

            <button
              onClick={() => navigate("/email/messages")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                            0px 36px 32px -16px #7F56D936,
                            0px 8px 16px 0px #7F56D91F,
                            0px 2px 4px -8px #7F56D914,
                            0px 8px 24px -16px #FFFFFF3D inset,
                            0px -24px 32px 0px #FFFFFF38 inset,
                            0px 0px 12px 0px #FFFFFF inset
                          `,
                borderImageSlice: "1",
              }}
            >
              <Mail size={16} className="shrink-0" />
              <span className="whitespace-nowrap"> View All Emails</span>
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Database}
              title="Total Emails"
              value={stats.totalEmails || 0}
              iconBg="bg-blue-600"
            />
            <StatCard
              icon={AlertCircle}
              title="High Priority"
              value={stats.byPriority?.high || 0}
              iconBg="bg-orange-600"
            />
            <StatCard
              icon={Shield}
              title="High Risk"
              value={stats.byRiskLevel?.high || 0}
              iconBg="bg-red-600"
            />
            <StatCard
              icon={TrendingUp}
              title="Avg Sentiment"
              value={stats.averageSentimentScore?.toFixed(2) || "0.00"}
              iconBg="bg-green-600"
            />
          </div>
        )}

        {/* Connected Accounts */}
        <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6 mb-8">
          <div className="border-b border-gray-700 pb-4 mb-6">
            <h2 className="text-xl font-semibold text-white">
              Connected Accounts
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Manage your email account connections and sync settings
            </p>
          </div>

          {connections.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-6">No email accounts connected</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={() => connectEmail("microsoft")}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Connect Microsoft
                </button>
                <button
                  onClick={() => connectEmail("google")}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Connect Google
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {connections.map((conn) => (
                <div
                  key={conn.provider}
                  className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 bg-[#13161B] border border-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    {getProviderIcon(conn.provider)}
                    <div>
                      <h3 className="font-medium text-white capitalize text-lg">
                        {conn.provider} Email
                      </h3>
                      <p className="text-sm text-gray-400">
                        {conn.emailAddress}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-gray-500">
                          Last synced: {formatDate(conn.lastSyncedAt)}
                        </span>
                        {conn.isTokenValid ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                            Token Expired
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg">
                      <input
                        type="checkbox"
                        checked={conn.syncEnabled}
                        onChange={(e) =>
                          toggleSync(conn.provider, e.target.checked)
                        }
                        className="rounded border-gray-600 text-purple-600 focus:ring-purple-500 bg-[#13161B]"
                      />
                      <span className="text-sm text-gray-300">Auto-sync</span>
                    </label>

                    <button
                      onClick={() => syncNow(conn.provider)}
                      disabled={syncing[conn.provider]}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw
                        className={`w-4 h-4 ${
                          syncing[conn.provider] ? "animate-spin" : ""
                        }`}
                      />
                      Sync Now
                    </button>

                    <button
                      onClick={() => disconnectEmail(conn.provider)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}

              {connections.length < 2 && (
                <div className="text-center pt-4">
                  <button
                    onClick={() =>
                      connectEmail(
                        connections.find((c) => c.provider === "microsoft")
                          ? "google"
                          : "microsoft"
                      )
                    }
                    className="px-6 py-3 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Mail className="w-5 h-5 inline mr-2" />
                    Add Another Account
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Sync Logs */}
        {syncLogs.length > 0 && (
          <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6">
            <div className="border-b border-gray-700 pb-4 mb-6">
              <h2 className="text-xl font-semibold text-white">
                Recent Sync Activity
              </h2>
            </div>

            <div className="space-y-3">
              {syncLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-[#13161B] border border-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white capitalize">
                          {log.credential?.provider} Sync
                        </span>
                        {getSyncStatusBadge(log)}
                      </div>
                      <p className="text-sm text-gray-400 mt-1">
                        {log.messagesSynced} messages synced,{" "}
                        {log.messagesAnalyzed} analyzed
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(log.startedAt)}
                        {log.completedAt &&
                          ` - Completed in ${Math.round(
                            (new Date(log.completedAt) -
                              new Date(log.startedAt)) /
                              1000
                          )}s`}
                      </p>
                    </div>
                  </div>

                  {log.errorsCount > 0 && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                      {log.errorsCount} errors
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {/* <div className="mt-8 flex flex-wrap gap-4">
          <button
            onClick={() => navigate("/email/messages")}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Mail className="w-5 h-5" />
            View All Messages
          </button>
          <button
            onClick={() => navigate("/email/analytics")}
            className="flex items-center gap-2 px-6 py-3 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <TrendingUp className="w-5 h-5" />
            View Analytics
          </button>
          <button
            onClick={() => navigate("/email/legal-holds")}
            className="flex items-center gap-2 px-6 py-3 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Shield className="w-5 h-5" />
            Legal Holds
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default EmailIntegration;
